/**
 * Client-side profile photo preparation (E34). Pure logic plus a thin browser adapter, no dependencies.
 *
 * The raw pick may be a multi-megabyte phone photo, so the input limit (10 MiB) is far above the server's 2 MiB cap;
 * what is uploaded is always a 512×512 JPEG (q 0.9, ~30–150 KB), centre-cropped to a square with EXIF rotation applied.
 * This is a convenience, not a security control: the server re-validates type, size and the cooldown.
 */
export const AVATAR_OUTPUT_SIZE = 512;
export const AVATAR_JPEG_QUALITY = 0.9;
export const AVATAR_ACCEPTED_TYPES = ['image/jpeg', 'image/png'] as const;
export const AVATAR_MAX_INPUT_BYTES = 10 * 1024 * 1024;
/** Server cap (E34); a prepared file above it is never sent. */
export const AVATAR_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const AVATAR_COOLDOWN_DAYS = 30;

export type AvatarPrepareErrorCode = 'EMPTY' | 'UNSUPPORTED_TYPE' | 'TOO_LARGE' | 'CORRUPT';

export class AvatarPrepareError extends Error {
  constructor(readonly code: AvatarPrepareErrorCode) {
    super(code);
    this.name = 'AvatarPrepareError';
  }
}

/** Declared-type and size checks on the picked file. Throws `AvatarPrepareError`. */
export function validateAvatarFile(file: { type: string; size: number }): void {
  if (!file.size) throw new AvatarPrepareError('EMPTY');
  if (!(AVATAR_ACCEPTED_TYPES as readonly string[]).includes(file.type.toLowerCase())) {
    throw new AvatarPrepareError('UNSUPPORTED_TYPE');
  }
  if (file.size > AVATAR_MAX_INPUT_BYTES) throw new AvatarPrepareError('TOO_LARGE');
}

/** Largest centred square inside a `width`×`height` image. */
export function squareCrop(width: number, height: number): { sx: number; sy: number; side: number } {
  const side = Math.min(width, height);
  return { sx: Math.floor((width - side) / 2), sy: Math.floor((height - side) / 2), side };
}

/** A decoded, orientation-corrected image. */
export interface DecodedImage {
  width: number;
  height: number;
  draw(ctx: AvatarContext, sx: number, sy: number, side: number, size: number): void;
  close(): void;
}

export interface AvatarContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality: 'low' | 'medium' | 'high';
  fillRect(x: number, y: number, w: number, h: number): void;
}

export interface AvatarCanvas {
  width: number;
  height: number;
  getContext(kind: '2d'): AvatarContext | null;
  toBlob(cb: (blob: Blob | null) => void, type: string, quality: number): void;
}

export interface PrepareDeps {
  decode(blob: Blob): Promise<DecodedImage>;
  createCanvas(size: number): AvatarCanvas;
}

/** createImageBitmap with EXIF orientation; falls back to an <img> (Safari builds without `imageOrientation`). */
async function decodeBrowser(blob: Blob): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
      return {
        width: bmp.width,
        height: bmp.height,
        draw: (ctx, sx, sy, side, size) =>
          (ctx as unknown as CanvasRenderingContext2D).drawImage(bmp, sx, sy, side, side, 0, 0, size, size),
        close: () => bmp.close(),
      };
    } catch {
      /* fall through to the <img> path */
    }
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    // Modern browsers apply EXIF orientation to <img> by default (image-orientation: from-image).
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) throw new Error('empty image');
    return {
      width,
      height,
      draw: (ctx, sx, sy, side, size) =>
        (ctx as unknown as CanvasRenderingContext2D).drawImage(img, sx, sy, side, side, 0, 0, size, size),
      close: () => undefined,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

const browserDeps: PrepareDeps = {
  decode: decodeBrowser,
  createCanvas: (size) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    return canvas as unknown as AvatarCanvas;
  },
};

/**
 * Validates, decodes, centre-crops to a square and re-encodes to a 512×512 JPEG `File` ("avatar.jpg").
 * Throws `AvatarPrepareError` (EMPTY | UNSUPPORTED_TYPE | TOO_LARGE | CORRUPT).
 */
export async function prepareAvatar(file: File, deps: PrepareDeps = browserDeps): Promise<File> {
  validateAvatarFile(file);
  let image: DecodedImage;
  try {
    image = await deps.decode(file);
  } catch {
    throw new AvatarPrepareError('CORRUPT');
  }
  try {
    if (!(image.width > 0 && image.height > 0)) throw new AvatarPrepareError('CORRUPT');
    const { sx, sy, side } = squareCrop(image.width, image.height);
    const canvas = deps.createCanvas(AVATAR_OUTPUT_SIZE);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new AvatarPrepareError('CORRUPT');
    ctx.fillStyle = '#ffffff'; // JPEG has no alpha: flatten transparent PNGs onto white
    ctx.fillRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    image.draw(ctx, sx, sy, side, AVATAR_OUTPUT_SIZE);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', AVATAR_JPEG_QUALITY));
    if (!blob || blob.size === 0) throw new AvatarPrepareError('CORRUPT');
    if (blob.size > AVATAR_MAX_UPLOAD_BYTES) throw new AvatarPrepareError('TOO_LARGE');
    return new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
  } catch (e) {
    throw e instanceof AvatarPrepareError ? e : new AvatarPrepareError('CORRUPT');
  } finally {
    image.close();
  }
}

/** Rolling-window helpers (the server is authoritative; these only drive the UI). */
export function cooldownRemainingMs(nextChangeAtIso: string | null | undefined, nowMs: number): number {
  if (!nextChangeAtIso) return 0;
  const at = new Date(nextChangeAtIso).getTime();
  return Number.isFinite(at) ? Math.max(0, at - nowMs) : 0;
}

export function splitCountdown(ms: number): { days: number; hours: number; minutes: number; seconds: number } {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}
